import { useState, useEffect } from "react";
import PredicateForceGraph from "./PredicateForceGraph";
import ProductParametersPanel from "./ProductParametersPanel";
import { filterByProductCode, filterCandidates } from "../../utils/searchResults";

const PredicateBubbleMap = ({ 
        searchResults, 
        setSearchResults,
        selectedProductCode,
        showPredicateBubbleMap, 
        setShowPredicateBubbleMap, 
        setShowProductCodes,
        setShowPredicateCandidates
    }) => {
    const [selectedKNumber, setSelectedKNumber] = useState("");
    const [selectedProduct, setSelectedProduct] = useState(null);

    useEffect(() => {
        // Set example product when a K-Number is selected
        if (selectedKNumber) {
            // console.log("Selected K Number:", selectedKNumber);

            const product = searchResults.find((result) => result.kNumber === selectedKNumber);
            if (product) {
              setSelectedProduct(product);
            }
            else {
              // Gray node
              setSelectedProduct({ kNumber: selectedKNumber, deviceName: "This K number is not yet in the system.", characteristics: [] });
            }
        } else {
            setSelectedProduct(null);
        }
    }, [selectedKNumber]);

    const handleBackToProductCodes = () => {
        setShowPredicateBubbleMap(false);
        setShowProductCodes(true);
    }

    const handleShowPredicatesTable = () => {
        const predicateCandidates = filterCandidates(searchResults);
        if (predicateCandidates.length === 0) {
            alert("You have to click Yes on the product details panel to add a predicate candidate to the table.");
            return;
        }

        setShowPredicateBubbleMap(false);
        setShowPredicateCandidates(true);
    }

    const setCandidacy = (kNumber, yesMaybeNo) => {
        if (kNumber) {
          const product = searchResults.find((result) => result.kNumber === selectedKNumber);
          if (product) {
            setSearchResults(prevResults =>
              prevResults.map(item =>
                item.kNumber === selectedKNumber ? { ...item, isCandidate: yesMaybeNo } : item
              )
            );
          }
        }
    };

    const handleYes = (selectedKNumber) => {
        // console.log("Yes for predicate candidate:", selectedKNumber);
        setCandidacy(selectedKNumber, "Yes");
    };

    const handleMaybe = (selectedKNumber) => {
        // console.log("Maybe for predicate candidate:", selectedKNumber);
        setCandidacy(selectedKNumber, "Maybe");
    };

    const handleNo = (selectedKNumber) => {
        // console.log("No for predicate candidate:", selectedKNumber);
        setCandidacy(selectedKNumber, "No");
    };

    const predicateCandidates = filterCandidates(searchResults);

    const [detailsWidth, setDetailsWidth] = useState(360);
    const [isResizing, setIsResizing] = useState(false);

    useEffect(() => {
      function handleMouseMove(e) {
      if (!isResizing) return;
      const container = document.getElementById("bubble-map-and-details-container");
      if (!container) return;
      const rect = container.getBoundingClientRect();
      // keep min and max so graph stays usable
      const min = 220;
      const max = rect.width - 220;
      const newWidth = Math.min(Math.max(e.clientX - rect.left, min), max);
      setDetailsWidth(newWidth);
      }
      function handleMouseUp() {
      if (isResizing) setIsResizing(false);
      }
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      };
    }, [isResizing]);

    return (
      <div id="predicate-bubble-map-panel">
        <div id="bubble-map-header">
        <div className="feature-title">
          <div className="feature-content-heading">Click a bubble to see product details for that K number</div>
          <p>Use mouse scroll over bubble graph to zoom in and out</p>
          <div className="predicate-bubble-map-button-bar">
          <button
            type="button"
            className="predicate-bubble-map-button"
            onClick={handleBackToProductCodes}
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor = "var(--ACCELIDEA_MID_GREEN, #038C5A)")
            }
            onMouseOut={(e) =>
              (e.currentTarget.style.backgroundColor = "var(--ACCELIDEA_DARK_GREEN, #025131)")
            }
          >
            Back to Product Codes
          </button>
          <button
            type="button"
            className="predicate-bubble-map-button"
            onClick={handleShowPredicatesTable}
            onMouseOver={(e) =>
              (e.currentTarget.style.backgroundColor = "var(--ACCELIDEA_MID_GREEN, #038C5A)")
            }
            onMouseOut={(e) =>
              (e.currentTarget.style.backgroundColor = "var(--ACCELIDEA_DARK_GREEN, #025131)")
            }
          >
            View Predicate Table {predicateCandidates.length > 0 ? `(${predicateCandidates.length})` : ""}
          </button>
          </div>
        </div>
        </div>

        <div
        id="bubble-map-and-details-container"
        style={{ display: "flex", alignItems: "stretch", height: "100%" }}
        >
        {selectedKNumber && selectedProduct && (
          <>
          <div
            id="bubble-map-product-details-panel"
            style={{
            width: detailsWidth,
            minWidth: 220,
            maxWidth: "70%",
            overflow: "auto",
            transition: isResizing ? "none" : "width 120ms",
            background: "var(--ACCELIDEA_PANEL_BG, #fff)"
            }}
          >
            <ProductParametersPanel 
              panelTitle={selectedProduct.device_name} 
              productKNumber={selectedProduct.kNumber}
              productCode={selectedProduct.productCode}
              fdaLetterUrl={selectedProduct.fdaLetterUrl}
              fields={[
              ...(selectedProduct.deviceName ? [{
              name: "deviceName",
              label: "Device Name",
              value: selectedProduct.deviceName
              }] : []),
              ...(selectedProduct.kNumber ? [{
              name: "kNumber",
              label: "K Number",
              value: selectedProduct.kNumber
              }] : []),
              ...(selectedProduct.intendedUse ? [{
              name: "intendedUse",
              label: "Intended Use",
              value: selectedProduct.intendedUse,
              type: "textarea"
              }] : []),
              ...selectedProduct.characteristics.map((char) => ({
              name: char.name,
              label: char.name,
              value: char.value,
              }))
              ]}
              isCandidate={selectedProduct.isCandidate}
              addYesMaybeNoButtons={true}
              handleYes={handleYes}
              handleMaybe={handleMaybe}
              handleNo={handleNo}
            />
          </div>

          {/* resizer */}
          <div
            role="separator"
            aria-orientation="vertical"
            title="Drag to resize"
            onMouseDown={() => setIsResizing(true)}
            onDoubleClick={() => setDetailsWidth(360)}
            style={{
            width: 8,
            cursor: "col-resize",
            background: "transparent",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            userSelect: "none"
            }}
          >
            <div style={{
            width: 2,
            height: 40,
            background: "rgba(0,0,0,0.12)",
            borderRadius: 2
            }} />
          </div>
          </>
        )}

        <div style={{ flex: 1, minWidth: 0 }}>
          <PredicateForceGraph 
            data={filterByProductCode(searchResults, selectedProductCode)} 
            onBubbleClick={setSelectedKNumber} 
            />
        </div>
        </div>
      </div>
    );
};

export default PredicateBubbleMap;
